// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState, useRef } from 'react';
import imageCompression from 'browser-image-compression';
import api from '../services/api';

/**
 * Componente PhotoUpload
 * 
 * Componente para upload de fotos com compressão no cliente e preview
 * 
 * @param {Object} props
 * @param {number} props.memberId - ID do membro
 * @param {string} props.currentPhotoUrl - URL da foto atual (opcional)
 * @param {string} props.currentThumbnailUrl - URL do thumbnail atual (opcional)
 * @param {Function} props.onFileSelect - Callback chamado quando arquivo é selecionado (opcional - se fornecido, não faz upload automático)
 * @param {Function} props.onUploadSuccess - Callback chamado após upload bem-sucedido
 * @param {Function} props.onUploadError - Callback chamado em caso de erro
 * @param {boolean} props.disabled - Se true, desabilita o upload
 */
function PhotoUpload({
    memberId,
    currentPhotoUrl = null,
    currentThumbnailUrl = null,
    onFileSelect = null,
    onUploadSuccess = null,
    onUploadError = null,
    disabled = false
}) {
    const [preview, setPreview] = useState(currentPhotoUrl || currentThumbnailUrl || null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const [originalSize, setOriginalSize] = useState(null);
    const [compressedSize, setCompressedSize] = useState(null);
    const fileInputRef = useRef(null);
    const dropZoneRef = useRef(null);

    /**
     * Compressa imagem no cliente antes de enviar
     */
    const compressImage = async (file) => {
        const options = {
            maxSizeMB: 2, // Máximo 2MB
            maxWidthOrHeight: 1200, // Máximo 1200px
            useWebWorker: true, // Não bloqueia UI
            fileType: 'image/webp', // Converte para WebP
        };

        try {
            const compressedFile = await imageCompression(file, options);

            // Renomeia o arquivo para ter extensão .webp quando convertido para WebP
            // Isso garante que o nome do arquivo corresponda ao tipo MIME
            const originalName = file.name;
            const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
            const renamedFile = new File([compressedFile], `${nameWithoutExt}.webp`, {
                type: 'image/webp',
                lastModified: Date.now()
            });

            return renamedFile;
        } catch (error) {
            console.error('Erro ao comprimir imagem:', error);
            throw new Error('Erro ao comprimir imagem. Tente novamente.');
        }
    };

    /**
     * Cria preview da imagem selecionada
     */
    const createPreview = (file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    /**
     * Processa arquivo selecionado
     */
    const processFile = async (file) => {
        // Valida tipo de arquivo
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            throw new Error('Tipo de arquivo não permitido. Use JPG, PNG ou WebP.');
        }

        // Valida tamanho (máximo 15MB antes de compressão)
        const maxSize = 15 * 1024 * 1024; // 15MB
        if (file.size > maxSize) {
            throw new Error('Arquivo muito grande. Tamanho máximo: 15MB.');
        }

        setOriginalSize(file.size);
        setError(null);

        // Cria preview
        createPreview(file);

        // Compressa imagem
        const compressedFile = await compressImage(file);
        setCompressedSize(compressedFile.size);

        return compressedFile;
    };

    /**
     * Faz upload da foto
     */
    const uploadPhoto = async (file) => {
        if (!memberId) {
            throw new Error('ID do membro não fornecido.');
        }

        setUploading(true);
        setProgress(0);
        setError(null);

        try {
            // Cria FormData
            const formData = new FormData();
            formData.append('photo', file);

            // Faz upload com progresso
            // Nota: Content-Type será definido automaticamente pelo axios para FormData
            const response = await api.post(
                `/members/${memberId}/photo`,
                formData,
                {
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setProgress(percentCompleted);
                    },
                }
            );

            if (response.data.success) {
                const { photo_url, photo_thumbnail_url } = response.data.data;
                setPreview(photo_url);

                if (onUploadSuccess) {
                    onUploadSuccess({
                        photo_url,
                        photo_thumbnail_url,
                    });
                }
            } else {
                throw new Error(response.data.error?.message || 'Erro ao fazer upload da foto.');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error?.message
                || error.response?.data?.message
                || error.message
                || 'Erro ao fazer upload da foto. Tente novamente.';

            setError(errorMessage);

            if (onUploadError) {
                onUploadError(errorMessage);
            }
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    /**
     * Manipula seleção de arquivo
     */
    const handleFileSelect = async (file) => {
        try {
            const compressedFile = await processFile(file);

            // Se foi passada função de callback para seleção, usa ela e não faz upload
            if (onFileSelect) {
                onFileSelect(compressedFile);
                return;
            }

            // Caso contrário, faz upload normal
            await uploadPhoto(compressedFile);
        } catch (error) {
            setError(error.message);
            setPreview(null);
            setOriginalSize(null);
            setCompressedSize(null);

            if (onUploadError) {
                onUploadError(error.message);
            }
        }
    };

    /**
     * Manipula drag and drop
     */
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled || uploading) {
            return;
        }

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    /**
     * Manipula drag over
     */
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    /**
     * Manipula click no botão de seleção
     */
    const handleButtonClick = () => {
        if (!disabled && !uploading) {
            fileInputRef.current?.click();
        }
    };

    /**
     * Manipula mudança no input de arquivo
     */
    const handleInputChange = (e) => {
        const files = e.target.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
        // Limpa o input para permitir selecionar o mesmo arquivo novamente
        e.target.value = '';
    };

    /**
     * Formata tamanho de arquivo
     */
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="photo-upload">
            {/* Zona de Drop */}
            <div
                ref={dropZoneRef}
                className={`photo-upload-dropzone ${disabled || uploading ? 'disabled' : ''} ${error ? 'error' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={handleButtonClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleInputChange}
                    disabled={disabled || uploading}
                    style={{ display: 'none' }}
                />

                {uploading ? (
                    <div className="photo-upload-progress">
                        <div className="photo-upload-progress-bar">
                            <div
                                className="photo-upload-progress-fill"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <p className="photo-upload-progress-text">
                            Enviando... {progress}%
                        </p>
                    </div>
                ) : preview ? (
                    <div className="photo-upload-preview">
                        <img src={preview} alt="Preview" />
                        {!disabled && (
                            <div className="photo-upload-overlay">
                                <span className="material-symbols-outlined">camera_alt</span>
                                <p>Clique para alterar</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="photo-upload-placeholder">
                        <span className="material-symbols-outlined">add_photo_alternate</span>
                        <p>Arraste uma foto aqui ou clique para selecionar</p>
                        <p className="photo-upload-hint">
                            JPG, PNG ou WebP • Máximo 15MB
                        </p>
                    </div>
                )}
            </div>

            {/* Informações de tamanho */}
            {(originalSize || compressedSize) && (
                <div className="photo-upload-info">
                    {originalSize && (
                        <span className="photo-upload-size original">
                            Original: {formatFileSize(originalSize)}
                        </span>
                    )}
                    {compressedSize && (
                        <span className="photo-upload-size compressed">
                            Comprimido: {formatFileSize(compressedSize)}
                            {originalSize && (
                                <span className="photo-upload-savings">
                                    {' '}(-{Math.round((1 - compressedSize / originalSize) * 100)}%)
                                </span>
                            )}
                        </span>
                    )}
                </div>
            )}

            {/* Mensagem de erro */}
            {error && (
                <div className="alert alert-error photo-upload-error">
                    {error}
                </div>
            )}
        </div>
    );
}

export default PhotoUpload;

